import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card, ListGroup, Badge } from 'react-bootstrap';
import { bursariesAPI } from '../services/api';

const BursaryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    contact_email: '',
    contact_phone: '',
    application_address: '',
    closing_date: ''
  });
  const [files, setFiles] = useState<FileList | null>(null);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadBursary();
    }
  }, [id]);

  const loadBursary = async () => {
    try {
      const response = await bursariesAPI.getById(String(id));
      const bursary = response.data;
      setFormData({
        title: bursary.title,
        description: bursary.description || '',
        requirements: bursary.requirements || '',
        contact_email: bursary.contact_email || '',
        contact_phone: bursary.contact_phone || '',
        application_address: bursary.application_address || '',
        closing_date: bursary.closing_date ? bursary.closing_date.split('T')[0] : ''
      });
      setExistingDocs(bursary.documents || []);
    } catch (err) {
      setError('Failed to load bursary');
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (files) {
        Array.from(files).forEach(file => {
          data.append('documents', file);
        });
      }

      if (isEdit) {
        await bursariesAPI.update(String(id), data);
      } else {
        await bursariesAPI.create(data);
      }

      navigate('/bursaries');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save bursary');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (window.confirm('Delete this document?')) {
      try {
        await bursariesAPI.deleteDocument(String(docId));
        setExistingDocs(existingDocs.filter(doc => doc.id !== docId));
      } catch (err) {
        setError('Failed to delete document');
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '3rem' }}>
      <Container style={{ maxWidth: '900px' }}>
        <div className="mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            {isEdit ? 'Edit Bursary' : 'Create New Bursary'}
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px', margin: 0 }}>
            {isEdit ? 'Update bursary information and documents' : 'Fill in the details to add a new bursary'}
          </p>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '24px'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '32px' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>
                  Title <span style={{ color: '#ff4d4f' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="Enter bursary title"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Provide a detailed description of the bursary"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Requirements</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="List the eligibility requirements"
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  List the eligibility requirements for this bursary
                </Form.Text>
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Contact Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Contact Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="+264 XX XXX XXXX"
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Application Address</Form.Label>
                <Form.Control
                  type="text"
                  name="application_address"
                  value={formData.application_address}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Physical or email address for applications"
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Physical or email address for submitting applications
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Closing Date</Form.Label>
                <Form.Control
                  type="date"
                  name="closing_date"
                  value={formData.closing_date}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Upload Documents</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e: any) => setFiles(e.target.files)}
                  style={inputStyle}
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Upload application forms, brochures, or other relevant documents
                </Form.Text>
              </Form.Group>

              {isEdit && existingDocs.length > 0 && (
                <div className="mb-4">
                  <Form.Label style={labelStyle}>Existing Documents</Form.Label>
                  <ListGroup style={{ borderRadius: '6px', overflow: 'hidden' }}>
                    {existingDocs.map(doc => (
                      <ListGroup.Item 
                        key={doc.id} 
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          border: '1px solid #f0f0f0',
                          padding: '12px 16px',
                          backgroundColor: 'white'
                        }}
                      >
                        <div>
                          <span style={{ color: '#262626', fontSize: '14px' }}>{doc.file_name}</span>
                          {doc.file_type && (
                            <Badge 
                              bg="secondary" 
                              className="ms-2"
                              style={{
                                backgroundColor: '#f0f0f0',
                                color: '#595959',
                                fontWeight: 500,
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                            >
                              {doc.file_type}
                            </Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff1f0';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          Delete
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              <div className="d-flex gap-2 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                <Button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#004a85';
                  }}
                  onMouseOut={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#00579a';
                  }}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Bursary' : 'Create Bursary')}
                </Button>
                <Button 
                  onClick={() => navigate('/bursaries')}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#00579a';
                    e.currentTarget.style.color = '#00579a';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
  transition: 'all 0.2s ease'
};

export default BursaryForm;